import { z } from "zod";
import type { CreateClientRequest } from "@models/organization";
import { addressFormFields, requiredText } from "./addressForm";
import { resaleCertificateError } from "./locationForm";
import { organizationFormSchema } from "./organizationForm";

// The New Client form (C3, API PR 419): the organization form (C5) plus the
// tax id and the first location's resale certificate, and, when the first
// location is not the organization's, that location's name and address in a
// form of its own (firstLocationFormSchema).

// Mirrors DomainConstraint on the API (Organization.TaxId, Location.Name), so
// a length CreateClientValidator would refuse with a 400 is caught inline.
const MAX = {
    taxId: 64,
    locationName: 256,
} as const;

// The marketplace is US-only (every state is one of the 50 + DC), and the form
// has no country field.
const COUNTRY = "US";

export const createClientFormSchema = organizationFormSchema.extend({
    taxId: requiredText("Tax ID", MAX.taxId),
    // The FileList the file input hands react-hook-form; undefined until a
    // file is chosen, which the shared rule refuses.
    resaleCertificate: z.custom<FileList>().superRefine((files, context) => {
        const message = resaleCertificateError(files?.[0]);

        if (message) {
            context.addIssue({ code: "custom", message });
        }
    }),
});

export type CreateClientFormValues = z.infer<typeof createClientFormSchema>;

export const firstLocationFormSchema = z.object({
    locationName: requiredText("Location name", MAX.locationName),
    ...addressFormFields,
});

export type FirstLocationFormValues = z.infer<typeof firstLocationFormSchema>;

// Lead call (C3/C4 follow-up): the first location defaults to the
// organization's name and address, so its own form starts from them.
export const toFirstLocationFormValues = (
    values: Pick<
        CreateClientFormValues,
        "name" | "streetAddress" | "city" | "state" | "zipCode"
    >,
): FirstLocationFormValues => ({
    locationName: values.name,
    streetAddress: values.streetAddress,
    city: values.city,
    state: values.state,
    zipCode: values.zipCode,
});

// `location` null: the first location is the organization's name and address.
// `confirmDuplicateTaxId`: the admin was warned another client uses this tax
// id (tax_id_in_use) and creates the client anyway.
export const toCreateClientRequest = (
    values: CreateClientFormValues,
    location: FirstLocationFormValues | null,
    confirmDuplicateTaxId: boolean,
): CreateClientRequest => {
    const firstLocation = location ?? toFirstLocationFormValues(values);

    return {
        name: values.name,
        legalBusinessName: values.legalBusinessName,
        taxId: values.taxId,
        streetAddress: values.streetAddress,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
        country: COUNTRY,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        locationName: firstLocation.locationName,
        locationStreetAddress: firstLocation.streetAddress,
        locationCity: firstLocation.city,
        locationState: firstLocation.state,
        locationZipCode: firstLocation.zipCode,
        locationCountry: COUNTRY,
        resaleCertificate: values.resaleCertificate[0],
        confirmDuplicateTaxId,
    };
};
